/**
 * pa-marquee-bar.js — PA • Marquee Bar
 *
 * Clones the message group until the strip is wider than the bar
 * plus one extra group, then sets the animation duration so the
 * scroll moves at the merchant's chosen pixels-per-second speed.
 * The CSS keyframes slide every group left by its own width,
 * which loops seamlessly because all groups are identical.
 */

if (!customElements.get('pa-marquee-bar')) {
  customElements.define(
    'pa-marquee-bar',
    class PaMarqueeBar extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.pa-marquee-bar__track');
        this.group = this.querySelector('.pa-marquee-bar__group');
        if (!this.track || !this.group) return;

        this.speed = parseInt(this.dataset.speed, 10) || 40;
        this.lastWidth = 0;
        this.frame = null;

        this.layout();

        // Widths change once web fonts load and when the viewport resizes.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.layout());
        }
        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.resizeObserver.observe(this);

        // Theme editor: hold still while a message block is being edited.
        this.onBlockSelect = (event) => {
          if (this.contains(event.target)) this.classList.add('is-paused');
        };
        this.onBlockDeselect = (event) => {
          if (this.contains(event.target)) this.classList.remove('is-paused');
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      scheduleLayout() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.clientWidth !== this.lastWidth) this.layout();
        });
      }

      layout() {
        this.lastWidth = this.clientWidth;
        // Drop the animation so original and fresh clones restart in sync;
        // reading offsetWidth below forces the style flush that resets it.
        this.removeAttribute('data-ready');
        this.track.querySelectorAll('.pa-marquee-bar__group[aria-hidden="true"]').forEach((clone) => clone.remove());

        const groupWidth = this.group.offsetWidth;
        if (!groupWidth) return;

        // Enough copies to cover the bar, plus one to scroll into view.
        const copies = Math.ceil(this.clientWidth / groupWidth);
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < copies; i++) {
          const clone = this.group.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
          clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          fragment.appendChild(clone);
        }
        this.track.appendChild(fragment);

        this.style.setProperty('--pa-marquee-duration', `${groupWidth / this.speed}s`);
        this.setAttribute('data-ready', '');
      }
    }
  );
}
